import { HttpClient, HttpContext } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { forkJoin, Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../../../environments/environment';
import { SUPPRESS_ERROR_TOAST } from './http-error.service';

export type AiSeverity = 'info' | 'warning' | 'critical';
export type AiPriority = 'low' | 'medium' | 'high';
export type AiTrend = 'rising' | 'falling' | 'stable' | 'volatile';

export interface AiDataPointContext {
    casdu: number;
    ioa: number;
    current_value?: unknown;
    trend?: AiTrend;
}

export interface AiRecommendationAction {
    title: string;
    priority: AiPriority;
    rationale: string;
}

export interface AiRecommendationResponse {
    severity: AiSeverity;
    summary: string;
    context: AiDataPointContext;
    actions: AiRecommendationAction[];
    knowledge_refs: string[];
}

export interface AiExplainResponse {
    casdu: number;
    ioa: number;
    current_value: unknown;
    trend: string;
    explanation: string;
}

export interface AiAnalyzeResponse {
    casdu: number;
    ioa: number;
    window_minutes: number;
    statistics: Record<string, number | string | null>;
    analysis: string;
    anomalies: string[];
}

export interface AiRecommendRequest {
    casdu: number;
    ioa: number;
    current_value?: unknown;
    trend?: AiTrend;
}

export interface AiAnalyzeRequest {
    casdu: number;
    ioa: number;
    window_minutes: number;
}

export interface AiChatRequest {
    message: string;
    casdu?: number;
    ioa?: number;
}

export interface AiChatStreamEvent {
    type: 'token' | 'done' | 'event';
    token?: string;
    raw?: unknown;
}

export interface AiHealthStatus {
    aiService: boolean;
    hailo: boolean;
    chromaDb: boolean;
    valkey: boolean;
    knowledgeDocuments: number;
    llmProvider: string;
    model: string;
}

export type KnowledgeSyncState = 'Pending' | 'Synced' | 'Failed';

/** A knowledge base: the management AI service or a knowledge-base capable edge device. */
export interface KnowledgeTarget {
    id: string;
    name: string;
    kind: 'management' | 'edge';
    online: boolean | null;
    site?: string | null;
    documentCount: number;
}

export interface KnowledgeDocument {
    id: string;
    target: string;
    fileName: string;
    contentType: string;
    sha256: string;
    sizeBytes: number;
    uploadedBy?: string | null;
    uploadedAt: string;
    syncState: KnowledgeSyncState;
    syncError?: string | null;
    chunkCount?: number | null;
    lastSyncedAt?: string | null;
}

export interface KnowledgeRebuildResult {
    target: string;
    documents: number;
    synced: number;
    pending: number;
    failed: number;
}

/** Must match the backend limit (KnowledgeService.MaxDocumentBytes). */
export const KNOWLEDGE_MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
export const KNOWLEDGE_ALLOWED_EXTENSIONS = ['.pdf', '.md', '.csv', '.txt'];

const HEALTH_CHECK_INTERVAL_MS = 30000;

@Injectable({
    providedIn: 'root'
})
export class AiInsightsService {
    private readonly http = inject(HttpClient);
    private readonly msalService = inject(MsalService);
    private readonly baseUrl = environment.aiApiUrl || '/api/ai';
    private readonly knowledgeBaseUrl = `${environment.apiConfig.uri}/knowledge`;
    private monitoringStarted = false;

    private readonly _health = signal<AiHealthStatus>({
        aiService: false,
        hailo: false,
        chromaDb: false,
        valkey: false,
        knowledgeDocuments: 0,
        llmProvider: '',
        model: '',
    });

    readonly healthStatus = computed(() => this._health());
    readonly isAvailable = computed(() => this._health().aiService);

    startHealthMonitoring(): void {
        if (this.monitoringStarted) {
            return;
        }
        this.monitoringStarted = true;

        timer(0, HEALTH_CHECK_INTERVAL_MS).pipe(
            switchMap(() => this.getCombinedHealth().pipe(
                catchError(() => of(this.offlineHealthStatus()))
            )),
            tap(status => this._health.set(status))
        ).subscribe();
    }

    refreshHealth(): Observable<AiHealthStatus> {
        return this.getCombinedHealth().pipe(
            catchError(() => of(this.offlineHealthStatus())),
            tap(status => this._health.set(status))
        );
    }

    getHealth(): Observable<AiHealthStatus> {
        return this.http.get<Record<string, unknown>>(`${this.baseUrl}/health`, {
            context: new HttpContext().set(SUPPRESS_ERROR_TOAST, true)
        }).pipe(
            map(raw => this.normalizeHealth(raw))
        );
    }

    getModels(): Observable<string[]> {
        return this.http.get<string[]>(`${this.baseUrl}/models`);
    }

    getKnowledgeStatus(): Observable<Record<string, unknown>> {
        return this.http.get<Record<string, unknown>>(`${this.baseUrl}/knowledge/status`, {
            context: new HttpContext().set(SUPPRESS_ERROR_TOAST, true)
        });
    }

    /**
     * Knowledge bases are managed by the management backend (it keeps the originals and syncs
     * them to the AI services). Uses the management API base URL so MSAL attaches the token;
     * /api/ai/ would be routed straight to the AI service.
     */
    getKnowledgeTargets(): Observable<KnowledgeTarget[]> {
        return this.http.get<KnowledgeTarget[]>(`${this.knowledgeBaseUrl}/targets`);
    }

    getKnowledgeDocuments(target: string): Observable<KnowledgeDocument[]> {
        return this.http.get<KnowledgeDocument[]>(
            `${this.knowledgeBaseUrl}/targets/${encodeURIComponent(target)}/documents`);
    }

    uploadKnowledgeDocument(target: string, file: File): Observable<KnowledgeDocument> {
        const formData = new FormData();
        formData.append('file', file, file.name);
        return this.http.post<KnowledgeDocument>(
            `${this.knowledgeBaseUrl}/targets/${encodeURIComponent(target)}/documents`, formData);
    }

    deleteKnowledgeDocument(target: string, documentId: string): Observable<void> {
        return this.http.delete<void>(
            `${this.knowledgeBaseUrl}/targets/${encodeURIComponent(target)}/documents/${encodeURIComponent(documentId)}`);
    }

    rebuildKnowledge(target: string): Observable<KnowledgeRebuildResult> {
        return this.http.post<KnowledgeRebuildResult>(
            `${this.knowledgeBaseUrl}/targets/${encodeURIComponent(target)}/rebuild`, {});
    }

    recommend(request: AiRecommendRequest): Observable<AiRecommendationResponse> {
        const payload = {
            context: {
                casdu: request.casdu,
                ioa: request.ioa,
                current_value: request.current_value ?? null,
                trend: request.trend ?? 'stable'
            }
        };
        return this.http.post<AiRecommendationResponse>(`${this.baseUrl}/recommend`, payload);
    }

    explain(casdu: number, ioa: number): Observable<AiExplainResponse> {
        return this.http.get<AiExplainResponse>(`${this.baseUrl}/explain/${casdu}/${ioa}`);
    }

    analyze(request: AiAnalyzeRequest): Observable<AiAnalyzeResponse> {
        return this.http.post<AiAnalyzeResponse>(`${this.baseUrl}/analyze`, request);
    }

    chatStream(request: AiChatRequest): Observable<AiChatStreamEvent> {
        return new Observable<AiChatStreamEvent>(observer => {
            const controller = new AbortController();

            (async () => {
                try {
                    // Acquire token so the AI backend can forward it to the management API
                    const headers: Record<string, string> = {
                        'Content-Type': 'application/json',
                        'Accept': 'text/event-stream',
                    };
                    try {
                        const accounts = this.msalService.instance.getAllAccounts();
                        if (accounts.length > 0) {
                            const tokenResponse = await this.msalService.instance.acquireTokenSilent({
                                scopes: environment.apiConfig.scopes,
                                account: accounts[0],
                            });
                            headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
                        }
                    } catch { /* proceed without token if acquisition fails */ }

                    const response = await fetch(`${this.baseUrl}/chat`, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify(request),
                        signal: controller.signal,
                    });

                    if (!response.ok || !response.body) {
                        throw new Error(`AI chat request failed (${response.status})`);
                    }

                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();
                    let buffer = '';

                    while (true) {
                        const { value, done } = await reader.read();
                        if (done) {
                            break;
                        }

                        buffer += decoder.decode(value, { stream: true });

                        let eventBoundary = buffer.indexOf('\n\n');
                        while (eventBoundary !== -1) {
                            const eventChunk = buffer.slice(0, eventBoundary).trim();
                            buffer = buffer.slice(eventBoundary + 2);
                            this.processSseChunk(eventChunk, observer);
                            eventBoundary = buffer.indexOf('\n\n');
                        }
                    }

                    observer.next({ type: 'done' });
                    observer.complete();
                } catch (error) {
                    if (!controller.signal.aborted) {
                        observer.error(error);
                    }
                }
            })();

            return () => controller.abort();
        });
    }

    private processSseChunk(chunk: string, observer: { next: (value: AiChatStreamEvent) => void; complete: () => void }): void {
        if (!chunk) {
            return;
        }

        const lines = chunk.split('\n');
        let doneReceived = false;

        for (const line of lines) {
            if (!line.startsWith('data:')) {
                continue;
            }

            const payload = line.slice(5).trim();
            if (!payload) {
                continue;
            }

            if (payload === '[DONE]') {
                doneReceived = true;
                continue;
            }

            try {
                const parsed = JSON.parse(payload) as Record<string, unknown> | string;
                if (typeof parsed === 'string') {
                    observer.next({ type: 'token', token: parsed });
                    continue;
                }

                const token = parsed['token'] ?? parsed['delta'] ?? parsed['content'];
                if (typeof token === 'string' && token.length > 0) {
                    observer.next({ type: 'token', token, raw: parsed });
                } else {
                    observer.next({ type: 'event', raw: parsed });
                }
            } catch {
                observer.next({ type: 'token', token: payload });
            }
        }

        if (doneReceived) {
            observer.next({ type: 'done' });
            observer.complete();
        }
    }

    private normalizeHealth(raw: Record<string, unknown>): AiHealthStatus {
        const aiService = this.readBoolean(raw, ['aiService', 'ai_service', 'healthy', 'available', 'service']) || this.readStatusText(raw);

        return {
            aiService,
            hailo: this.readBoolean(raw, ['hailo', 'hailo_available', 'hailoReady']),
            chromaDb: this.readBoolean(raw, ['chromadb', 'chromaDb', 'chroma', 'chromadb_available']),
            valkey: this.readBoolean(raw, ['valkey', 'redis', 'valkey_available']),
            knowledgeDocuments: this.readNumber(raw, ['knowledge_documents', 'knowledgeDocs', 'documents', 'document_count']),
            llmProvider: this.readString(raw, ['llm_provider', 'llmProvider', 'provider']),
            model: this.readString(raw, ['model', 'llm_model', 'modelName', 'active_model']),
        };
    }

    private getCombinedHealth(): Observable<AiHealthStatus> {
        return forkJoin({
            health: this.getHealth(),
            knowledge: this.getKnowledgeStatus().pipe(catchError(() => of({})))
        }).pipe(
            map(({ health, knowledge }) => ({
                ...health,
                knowledgeDocuments: this.readNumber(knowledge, ['knowledge_documents', 'knowledgeDocs', 'documents', 'document_count', 'total_documents'])
                    || health.knowledgeDocuments
            }))
        );
    }

    private readStatusText(raw: Record<string, unknown>): boolean {
        const status = raw['status'];
        if (typeof status !== 'string') {
            return false;
        }

        const normalized = status.toLowerCase();
        return normalized === 'ok' || normalized === 'healthy' || normalized === 'up';
    }

    private readBoolean(raw: Record<string, unknown>, keys: string[]): boolean {
        for (const key of keys) {
            const value = raw[key];
            if (typeof value === 'boolean') {
                return value;
            }
            if (typeof value === 'string') {
                const normalized = value.toLowerCase();
                if (normalized === 'true' || normalized === 'ok' || normalized === 'healthy' || normalized === 'up') {
                    return true;
                }
                if (normalized === 'false' || normalized === 'down') {
                    return false;
                }
            }
        }
        return false;
    }

    private readNumber(raw: Record<string, unknown>, keys: string[]): number {
        for (const key of keys) {
            const value = raw[key];
            if (typeof value === 'number') {
                return value;
            }
            if (typeof value === 'string') {
                const parsed = Number(value);
                if (!Number.isNaN(parsed)) {
                    return parsed;
                }
            }
        }
        return 0;
    }

    private readString(raw: Record<string, unknown>, keys: string[]): string {
        for (const key of keys) {
            const value = raw[key];
            if (typeof value === 'string' && value.length > 0) {
                return value;
            }
        }
        return '';
    }

    private offlineHealthStatus(): AiHealthStatus {
        return {
            aiService: false,
            hailo: false,
            chromaDb: false,
            valkey: false,
            knowledgeDocuments: 0,
            llmProvider: '',
            model: '',
        };
    }
}
